import { useEffect, useMemo, useState, Fragment } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, TextField, MenuItem, CircularProgress, Alert,
  Chip, Avatar, Stack, Typography, InputAdornment, Button, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper, Checkbox,
  Autocomplete, TablePagination, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Receipt as BillIcon, Search as SearchIcon, Close as CloseIcon,
  Save as SaveIcon, Layers as ItemsIcon, AttachMoney as MoneyIcon,
  Tune as TuneIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
  ContentCopy as DuplicateIcon,
  AccountBalanceWallet as AccountIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const STATES = ['Day Student', 'Boarder', 'Both'];

// Auto-detect a sensible default academic year (e.g. if today is in Aug-Dec
// → "2026/2027", otherwise → "2025/2026"). Only used when opening a fresh form.
function defaultAcademicYear() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 7 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
}

const blankAccount = () => ({ item: '', note: '', lines: [] });
const blankForm = {
  classes: [],
  acc_yer: '',
  state:   'Day Student',
  accounts: [],
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatTile({ icon, label, value, tone = 'primary' }) {
  return (
    <Card sx={{ flex: 1, minWidth: 200 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, fontSize: 10,
          }}>{label}</Typography>
          <Typography variant="h5" fontWeight={800} lineHeight={1.2}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Single-account block inside the multi-account dialog ─────────
function AccountBlock({
  index, account, categories, subItems, usedCategories,
  onChangeCategory, onChangeNote, onToggleLine, onSetLineAmount, onRemove, locked,
}) {
  const subtotal = useMemo(
    () => account.lines.reduce((s, l) => s + (Number(l.amo) || 0), 0),
    [account.lines]
  );
  const isSelected = (name) => account.lines.some(l => l.narr === name);
  const getAmount  = (name) => account.lines.find(l => l.narr === name)?.amo ?? '';

  // Available categories = all minus those already used elsewhere in this form
  const available = categories.filter(c =>
    c.name === account.item || !usedCategories.includes(c.name)
  );

  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderColor: account.item ? 'primary.light' : 'divider' }}>
      <Box sx={{
        px: 2, py: 1.25,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <Avatar sx={{
          width: 30, height: 30, fontSize: 13, fontWeight: 800,
          bgcolor: 'primary.main', color: '#fff',
        }}>{index + 1}</Avatar>

        <TextField
          select size="small" label="Account"
          value={account.item}
          onChange={(e) => onChangeCategory(e.target.value)}
          disabled={locked}
          sx={{ minWidth: 220, flex: 1, bgcolor: '#fff' }}
        >
          <MenuItem value=""><em>— Pick a category —</em></MenuItem>
          {available.map((c) => (
            <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
          ))}
        </TextField>

        <Chip
          size="small" color="success" variant="outlined"
          icon={<MoneyIcon sx={{ fontSize: 14 }} />}
          label={`Subtotal ${fmtMoney(subtotal)}`}
          sx={{ fontWeight: 800 }}
        />

        {!locked && (
          <Tooltip title="Remove this account">
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {!account.item ? (
          <Typography variant="body2" color="text.secondary">
            Pick an account category above to set its sub-items.
          </Typography>
        ) : subItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sub-items registered. Use <strong>Manage Subaccounts</strong> in the dialog header.
          </Typography>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
          }}>
            {subItems.map((s) => {
              const selected = isSelected(s.name);
              return (
                <Paper key={s.trans_code} variant="outlined"
                  sx={{
                    p: 1.1, display: 'flex', alignItems: 'center', gap: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.05) : 'background.paper',
                  }}>
                  <Checkbox size="small" checked={selected}
                    onChange={() => onToggleLine(s.name)} />
                  <Typography variant="body2" fontWeight={selected ? 700 : 500} sx={{ flex: 1, minWidth: 0 }}>
                    {s.name}
                  </Typography>
                  <TextField size="small" type="number" placeholder="0.00"
                    value={getAmount(s.name)}
                    onChange={(e) => onSetLineAmount(s.name, e.target.value)}
                    disabled={!selected}
                    sx={{ width: 110, '& input': { textAlign: 'right' } }} />
                </Paper>
              );
            })}
          </Box>
        )}

        <TextField fullWidth size="small" multiline rows={1.5} sx={{ mt: 1.5 }}
          label="Note (optional)" value={account.note}
          onChange={(e) => onChangeNote(e.target.value)}
          placeholder="Anything to print under this account on the bill" />
      </Box>
    </Card>
  );
}

export default function AdmissionDetailsPage() {
  const [rows, setRows]          = useState([]);
  const [classes, setClasses]    = useState([]);
  const [categories, setCats]    = useState([]);
  const [subItems, setSubItems]  = useState([]);
  const [loading, setL]          = useState(true);
  const [error, setError]        = useState('');

  // Sub-items management mini-dialog
  const [subOpen, setSubOpen]    = useState(false);
  const [subName, setSubName]    = useState('');
  const [subEditing, setSubEd]   = useState(null);
  const [subBusy, setSubBusy]    = useState(false);
  const [subError, setSubError]  = useState('');

  // Filters
  const [q, setQ]                = useState('');
  const [filterCla, setFCla]     = useState('');
  const [filterYer, setFYer]     = useState('');
  const [filterCat, setFCat]     = useState('');
  const [page, setPage]          = useState(0);
  const [perPage, setPP]         = useState(10);

  // Dialog state
  const [open, setOpen]          = useState(false);
  const [editing, setEditing]    = useState(null);   // row being edited (single account)
  const [form, setForm]          = useState(blankForm);
  const [saving, setSaving]      = useState(false);
  const [expandedKey, setExp]    = useState(null);
  const [previewLines, setPL]    = useState({});
  const [previewLoading, setPL2] = useState(null);

  const load = async () => {
    setL(true);
    try {
      const params = {};
      if (q)         params.q       = q;
      if (filterCla) params.cla     = filterCla;
      if (filterYer) params.acc_yer = filterYer;
      if (filterCat) params.item    = filterCat;
      setRows((await API.get('/admission-details', { params })).data);
    } catch (e) { setError(e.response?.data?.message || 'Load failed'); }
    finally { setL(false); }
  };

  const loadSubItems = () => API.get('/admission-details/sub-items')
    .then(r => setSubItems(r.data)).catch(() => {});

  useEffect(() => {
    API.get('/classes').then(r => setClasses(r.data)).catch(() => {});
    API.get('/admission-details/categories').then(r => setCats(r.data)).catch(() => {});
    loadSubItems();
  }, []);
  useEffect(() => { setPage(0); }, [q, filterCla, filterYer, filterCat]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, filterCla, filterYer, filterCat]);

  // ── Open dialogs ───────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    const lastRow = rows[0];
    setForm({
      ...blankForm,
      acc_yer: lastRow?.acc_yer || defaultAcademicYear(),
      state:   'Day Student',
      accounts: [],
    });
    setError(''); setOpen(true);
  };

  const openDuplicate = async (r) => {
    setError('');
    try {
      const res = await API.get('/admission-details/items', {
        params: { cla: r.cla, te: r.te, acc_yer: r.acc_yer, item: r.item, state: r.state },
      });
      setEditing(null);
      setForm({
        classes: [],
        acc_yer: r.acc_yer,
        state:   r.state,
        accounts: [{
          item: r.item,
          note: res.data.note || '',
          lines: (res.data.items || []).map(i => ({ narr: i.narr, amo: i.amo })),
        }],
      });
      setOpen(true);
      swal.info(`"${r.item}" copied. Pick the target class(es) and add more accounts if needed.`,
        'Duplicating bill');
    } catch (e) { swal.error(swal.pickError(e, 'Failed to load source bill')); }
  };

  const openEdit = async (r) => {
    setError('');
    try {
      const res = await API.get('/admission-details/items', {
        params: { cla: r.cla, te: r.te, acc_yer: r.acc_yer, item: r.item, state: r.state },
      });
      setEditing(r);
      setForm({
        classes: [r.cla],
        acc_yer: r.acc_yer,
        state:   r.state,
        accounts: [{
          item: r.item,
          note: res.data.note || '',
          lines: (res.data.items || []).map(i => ({ narr: i.narr, amo: i.amo })),
        }],
      });
      setOpen(true);
    } catch (e) { swal.error(swal.pickError(e, 'Failed to load')); }
  };

  // Toggle a row's expanded state and lazy-load its line items
  const toggleExpand = async (r) => {
    const key = `${r.cla}|${r.te}|${r.acc_yer}|${r.item}|${r.state}`;
    if (expandedKey === key) { setExp(null); return; }
    setExp(key);
    if (previewLines[key]) return;
    setPL2(key);
    try {
      const res = await API.get('/admission-details/items', {
        params: { cla: r.cla, te: r.te, acc_yer: r.acc_yer, item: r.item, state: r.state },
      });
      setPL((p) => ({ ...p, [key]: res.data.items || [] }));
    } catch { setPL((p) => ({ ...p, [key]: [] })); }
    finally { setPL2(null); }
  };

  // ── Form helpers ──────────────────────────────────────────────
  const addAccount = () =>
    setForm((f) => ({ ...f, accounts: [...f.accounts, blankAccount()] }));

  const removeAccount = (i) =>
    setForm((f) => ({ ...f, accounts: f.accounts.filter((_, idx) => idx !== i) }));

  const updateAccount = (i, patch) =>
    setForm((f) => ({
      ...f,
      accounts: f.accounts.map((a, idx) => idx === i ? { ...a, ...patch } : a),
    }));

  const toggleLineFor = (i, name) =>
    setForm((f) => ({
      ...f,
      accounts: f.accounts.map((a, idx) => {
        if (idx !== i) return a;
        const exists = a.lines.some(l => l.narr === name);
        return {
          ...a,
          lines: exists
            ? a.lines.filter(l => l.narr !== name)
            : [...a.lines, { narr: name, amo: '' }],
        };
      }),
    }));

  const setLineAmountFor = (i, name, amo) =>
    setForm((f) => ({
      ...f,
      accounts: f.accounts.map((a, idx) =>
        idx !== i ? a
        : { ...a, lines: a.lines.map(l => l.narr === name ? { ...l, amo } : l) }
      ),
    }));

  const usedCategories = form.accounts.map(a => a.item).filter(Boolean);

  const grandTotal = useMemo(
    () => form.accounts.reduce(
      (sum, a) => sum + a.lines.reduce((s, l) => s + (Number(l.amo) || 0), 0), 0),
    [form.accounts]
  );

  // ── Submit ────────────────────────────────────────────────────
  const submit = async () => {
    if (form.classes.length === 0) { setError('Pick at least one class'); return; }
    if (form.accounts.length === 0) { setError('Add at least one account'); return; }
    if (!form.acc_yer.trim())       { setError('Academic year is required'); return; }

    // Each account must have a category and at least one line with an amount
    for (let i = 0; i < form.accounts.length; i++) {
      const a = form.accounts[i];
      if (!a.item)
        return setError(`Account #${i + 1}: pick a category`);
      const lines = a.lines.filter(l => l.narr && Number(l.amo) > 0);
      if (lines.length === 0)
        return setError(`Account "${a.item}": tick at least one sub-item with an amount`);
    }

    const accountsCount = form.accounts.length;
    const linesCount = form.accounts.reduce(
      (s, a) => s + a.lines.filter(l => l.narr && Number(l.amo) > 0).length, 0);

    const ok = await swal.confirm({
      title: editing ? 'Update admission bill?' : 'Save admission bills?',
      html: `These bills will apply to every selected class on student admission.<br/><br/>` +
            `<strong>${form.classes.length}</strong> class(es) · ` +
            `<strong>${accountsCount}</strong> account(s) · ` +
            `<strong>${linesCount}</strong> sub-item(s) · ` +
            `Total: <strong>${fmtMoney(grandTotal)}</strong>`,
      confirmText: editing ? 'Save changes' : 'Yes, save all',
      icon: 'question',
      tone: 'primary',
    });
    if (!ok) return;

    setSaving(true); setError('');
    try {
      await API.post('/admission-details/bulk', {
        classes: form.classes,
        te:      '',
        acc_yer: form.acc_yer,
        state:   form.state,
        accounts: form.accounts.map(a => ({
          item: a.item,
          note: a.note,
          lines: a.lines.filter(l => l.narr && Number(l.amo) > 0),
        })),
      });
      if (editing) {
        setOpen(false);
        swal.success('Admission bill updated');
      } else {
        // Keep year/state for the next round; clear classes & accounts
        setForm((f) => ({ ...f, classes: [], accounts: [] }));
        swal.success(`${accountsCount} account(s) saved across ${form.classes.length} class(es)`);
      }
      await load();
    } catch (e) {
      setError(swal.pickError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    const ok = await swal.confirm({
      title: 'Delete admission bill?',
      html: `<strong>${r.cla}</strong> · ${r.acc_yer}<br/>` +
            `<small>This will remove all ${r.line_count} sub-account(s) totalling ${fmtMoney(r.total)}.</small>`,
      confirmText: 'Yes, delete',
      tone: 'danger',
      icon: 'warning',
    });
    if (!ok) return;
    try {
      await API.delete('/admission-details', {
        params: { cla: r.cla, te: r.te, acc_yer: r.acc_yer, item: r.item, state: r.state },
      });
      swal.success('Admission bill deleted');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  // ── Sub-items CRUD ────────────────────────────────────────────
  const openSubManager = () => { setSubName(''); setSubEd(null); setSubError(''); setSubOpen(true); };
  const submitSubItem = async () => {
    const name = subName.trim();
    if (!name) { setSubError('Name is required'); return; }
    setSubBusy(true); setSubError('');
    try {
      if (subEditing) {
        await API.put(`/admission-details/sub-items/${subEditing.trans_code}`, { name });
      } else {
        await API.post('/admission-details/sub-items', { name });
      }
      setSubName(''); setSubEd(null);
      await loadSubItems();
    } catch (e) {
      setSubError(e.response?.data?.message || 'Save failed');
    } finally { setSubBusy(false); }
  };
  const removeSubItem = async (s) => {
    const ok = await swal.confirmDelete(`sub-account "${s.name}"`);
    if (!ok) return;
    try {
      await API.delete(`/admission-details/sub-items/${s.trans_code}`);
      await loadSubItems();
      // Drop any selected entries with that name across every account in the form
      setForm((f) => ({
        ...f,
        accounts: f.accounts.map(a => ({
          ...a, lines: a.lines.filter(l => l.narr !== s.name),
        })),
      }));
      swal.success(`"${s.name}" removed from registry`);
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const stats = useMemo(() => {
    const totalAmount = rows.reduce((s, r) => s + Number(r.total || 0), 0);
    const distinctClasses = new Set(rows.map(r => r.cla)).size;
    return { groups: rows.length, classes: distinctClasses, total: totalAmount };
  }, [rows]);

  const paged = rows.slice(page * perPage, page * perPage + perPage);
  const acc_yers = Array.from(new Set(rows.map(r => r.acc_yer).filter(Boolean))).sort().reverse();

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <ModuleHeader icon={<BillIcon sx={{ fontSize: 28 }} />}
        title="Admission Details"
        subtitle="Define every account and sub-item per class — applied to students on registration"
        actionLabel="New Admission Bill" actionIcon={<AddIcon />} onAction={openNew} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <StatTile icon={<BillIcon />}  label="Bill Sets"        value={stats.groups}  tone="primary" />
        <StatTile icon={<ItemsIcon />} label="Classes Covered"  value={stats.classes} tone="secondary" />
        <StatTile icon={<MoneyIcon />} label="Total Amount"     value={fmtMoney(stats.total)} tone="success" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search class or narration…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select label="Class" value={filterCla} onChange={(e) => setFCla(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.trans_code} value={c.class}>{c.class}</MenuItem>)}
          </TextField>
          <TextField select label="Year" value={filterYer} onChange={(e) => setFYer(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {acc_yers.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <TextField select label="Category" value={filterCat} onChange={(e) => setFCat(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All</MenuItem>
            {categories.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
          </TextField>
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        {loading ? <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell sx={{ width: 56 }}></TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell align="right">Total (GH¢)</TableCell>
                  <TableCell align="right" sx={{ width: 180 }}>Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {paged.map((r) => {
                    const key = `${r.cla}|${r.te}|${r.acc_yer}|${r.item}|${r.state}`;
                    const isOpen = expandedKey === key;
                    const lines = previewLines[key];
                    return (
                      <Fragment key={key}>
                        <TableRow hover sx={{
                          '& td': { py: 1, borderBottom: isOpen ? 0 : undefined },
                          bgcolor: isOpen ? (t) => alpha(t.palette.primary.main, 0.04) : 'transparent',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleExpand(r)}>
                          <TableCell>
                            <IconButton size="small" sx={{ p: 0.3 }}>
                              {isOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Avatar sx={{
                              width: 32, height: 32, fontSize: 11, fontWeight: 800,
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                              color: 'primary.main',
                              border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.22)}`,
                            }}>{(r.cla?.[0] || '?').toUpperCase()}</Avatar>
                          </TableCell>
                          <TableCell><Typography variant="body2" fontWeight={700}>{r.cla}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{r.acc_yer}</Typography>
                            {r.te && (
                              <Typography variant="caption" color="text.secondary">
                                {r.te.replace('_', ' ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={r.item}
                              color="primary" variant="outlined" sx={{ fontSize: 10.5, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={r.state}
                              color={r.state === 'Boarder' ? 'secondary' : r.state === 'Both' ? 'info' : 'default'}
                              variant="outlined" sx={{ fontSize: 10.5 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" icon={<ItemsIcon sx={{ fontSize: 12 }} />}
                              label={r.line_count}
                              sx={{ fontWeight: 700, height: 22 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={800} color="success.dark">
                              {fmtMoney(r.total)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Duplicate to another class">
                              <IconButton size="small" onClick={() => openDuplicate(r)}>
                                <DuplicateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '& > td': { p: 0, border: 0 } }}>
                          <TableCell colSpan={9}>
                            <Collapse in={isOpen} unmountOnExit timeout="auto">
                              <Box sx={{
                                px: 3, py: 2,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                                borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
                              }}>
                                <Typography variant="overline" color="primary" fontWeight={700} display="block" mb={1}>
                                  Sub Accounts
                                </Typography>
                                {previewLoading === key ? (
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                                    <CircularProgress size={14} />
                                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                                  </Stack>
                                ) : !lines || lines.length === 0 ? (
                                  <Typography variant="body2" color="text.disabled">No sub-accounts</Typography>
                                ) : (
                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                    gap: 1,
                                  }}>
                                    {lines.map(line => (
                                      <Stack key={line.trans_code} direction="row" alignItems="center"
                                        sx={{
                                          px: 1.5, py: 0.8, bgcolor: '#fff',
                                          border: '1px solid', borderColor: 'divider', borderRadius: 1.2,
                                        }}>
                                        <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                          {line.narr}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} color="primary.main"
                                          sx={{ fontFamily: 'monospace' }}>
                                          {fmtMoney(line.amo)}
                                        </Typography>
                                      </Stack>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <BillIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.disabled" gutterBottom>
                        {q || filterCla || filterYer || filterCat
                          ? 'No bill sets match your filters'
                          : 'No admission bills set up yet'}
                      </Typography>
                      {!q && !filterCla && !filterYer && !filterCat && (
                        <Button size="small" variant="contained" startIcon={<AddIcon />}
                          sx={{ mt: 1 }} onClick={openNew}>
                          Create the first one
                        </Button>
                      )}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div" count={rows.length}
              page={page} onPageChange={(_, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => { setPP(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows:"
              showFirstButton showLastButton
            />
          </>
        )}
      </Card>

      {/* Create / Edit dialog — multi-account */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2,
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {editing ? <EditIcon /> : <AddIcon />}
            <Typography variant="h6" fontWeight={700}>
              {editing ? `Edit Bill · ${editing.cla}` : 'New Admission Bill'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Manage the registered list of sub-accounts">
              <Button size="small" startIcon={<TuneIcon />} onClick={openSubManager}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} variant="outlined">
                Manage Subaccounts
              </Button>
            </Tooltip>
            <IconButton onClick={() => !saving && setOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default', p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Coverage section — applies to every account below */}
          <Card sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="overline" color="primary" fontWeight={700} display="block" mb={1.5}>
              Coverage · applies to every account below
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                multiple disableCloseOnSelect
                // Blanks filtered out: MUI's default getOptionLabel reads
                // `option.label`, so one null row in the list throws while
                // the dropdown is opening — and a render throw blanks the
                // whole page rather than just the dropdown.
                options={[...new Set((classes || []).map(c => c?.class).filter(Boolean))]}
                getOptionLabel={(o) => String(o ?? '')}
                isOptionEqualToValue={(o, v) => String(o) === String(v)}
                value={form.classes}
                onChange={(_, v) => setForm({ ...form, classes: v })}
                disabled={!!editing}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" color="primary" label={option} {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Classes"
                    helperText={editing ? 'Editing applies only to this class — create a new bill to add more classes'
                                        : 'Pick one or more classes — every account below will be saved for each'}
                    required />
                )}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Academic Year" value={form.acc_yer}
                  onChange={(e) => setForm({ ...form, acc_yer: e.target.value })}
                  placeholder="e.g. 2026/2027" required />
                <TextField select label="Student Type" value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })} required>
                  {STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Box>
            </Stack>
          </Card>

          {/* Accounts section — multi */}
          <Card sx={{ p: 2.5, mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
              <Typography variant="overline" color="primary" fontWeight={700}>
                Accounts ({form.accounts.length})
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="success" variant="outlined"
                  icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                  label={`Grand Total ${fmtMoney(grandTotal)}`}
                  sx={{ fontWeight: 800 }} />
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Add every account category that applies (School Fees, PTA, Coding, Other Payment…) and tick its sub-items.
              Each account has its own subtotal, and you save them all at once.
            </Typography>

            {form.accounts.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                <AccountIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  No accounts yet. Add one to get started.
                </Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addAccount}>
                  Add the first account
                </Button>
              </Paper>
            ) : (
              <>
                {form.accounts.map((a, i) => (
                  <AccountBlock
                    key={i}
                    index={i}
                    account={a}
                    categories={categories}
                    subItems={subItems}
                    usedCategories={usedCategories}
                    onChangeCategory={(v) => updateAccount(i, { item: v })}
                    onChangeNote={(v)     => updateAccount(i, { note: v })}
                    onToggleLine={(name)  => toggleLineFor(i, name)}
                    onSetLineAmount={(name, amo) => setLineAmountFor(i, name, amo)}
                    onRemove={() => removeAccount(i)}
                    locked={!!editing && i === 0}
                  />
                ))}
                <Button size="small" startIcon={<AddIcon />} variant="outlined"
                  onClick={addAccount} sx={{ mt: 1 }}>
                  Add Another Account
                </Button>
              </>
            )}
          </Card>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {form.accounts.length > 0 && (
              <>
                <strong>{form.accounts.length}</strong> account(s) ·{' '}
                <strong>{form.accounts.reduce((s, a) => s + a.lines.length, 0)}</strong> sub-item(s) ·{' '}
                Grand Total <strong style={{ color: '#2e7d32' }}>GH¢ {fmtMoney(grandTotal)}</strong>
              </>
            )}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
              {editing ? 'Save Changes' : 'Save All'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Manage Sub Items dialog */}
      <Dialog open={subOpen} onClose={() => !subBusy && setSubOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2,
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TuneIcon />
            <Typography variant="h6" fontWeight={700}>Sub Item Registry</Typography>
          </Stack>
          <IconButton onClick={() => setSubOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default', p: 2 }}>
          {subError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubError('')}>{subError}</Alert>}

          <Card sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <TextField fullWidth size="small"
                label={subEditing ? `Edit "${subEditing.name}"` : 'New sub-item name'}
                value={subName} onChange={(e) => setSubName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitSubItem()}
                placeholder="e.g. Tuition" autoFocus />
              <Button variant="contained" onClick={submitSubItem} disabled={subBusy || !subName.trim()}
                startIcon={subBusy ? <CircularProgress size={14} color="inherit" /> : (subEditing ? <SaveIcon /> : <AddIcon />)}>
                {subEditing ? 'Save' : 'Add'}
              </Button>
              {subEditing && (
                <Button onClick={() => { setSubEd(null); setSubName(''); setSubError(''); }}>Cancel</Button>
              )}
            </Stack>
          </Card>

          <Card>
            {subItems.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">No sub-items yet</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {subItems.map(s => (
                  <Stack key={s.trans_code} direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{s.name}</Typography>
                    <Tooltip title="Rename">
                      <IconButton size="small" color="primary"
                        onClick={() => { setSubEd(s); setSubName(s.name); setSubError(''); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => removeSubItem(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
          </Card>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.default' }}>
          <Button onClick={() => setSubOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
